import React, { useEffect, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { getFileDetail } from 'stores/WorkSpaceStoreAdapter';

import { useKnzFileResolver } from '../../KnzFileResolverContext';
import { KnzComponentProps } from '../../types';

/** 拡張子 → Prism の言語名。未知の拡張子は拡張子そのものを言語名にする。 */
const EXTENSION_LANGUAGE: Record<string, string> = {
  ts: 'typescript',
  tsx: 'tsx',
  js: 'javascript',
  jsx: 'jsx',
  py: 'python',
  java: 'java',
  kt: 'kotlin',
  go: 'go',
  rb: 'ruby',
  php: 'php',
  cs: 'csharp',
  cpp: 'cpp',
  c: 'c',
  rs: 'rust',
  sh: 'bash',
  bash: 'bash',
  sql: 'sql',
  json: 'json',
  yaml: 'yaml',
  yml: 'yaml',
  xml: 'xml',
  html: 'markup',
  css: 'css',
  scss: 'scss',
  md: 'markdown',
  knz: 'markdown',
  txt: 'text',
};

/** 拡張子文字列（先頭の . は無視）から Prism 言語名を求める */
const languageFromExtension = (extension: string): string => {
  const ext = extension.replace(/^\./, '').toLowerCase();
  return EXTENSION_LANGUAGE[ext] ?? ext ?? 'text';
};

/** 共通の枠（ヘッダー + ハイライト本文）を描画する内部コンポーネント */
const CodeFrame: React.FC<{
  language: string;
  title?: string;
  /** ヘッダー左に出す参照ファイル名（link 参照時） */
  fileName?: string;
  /** 参照ファイルの絶対パス（link 参照時のみ。ホバーでツールチップ表示） */
  filePath?: string;
  code: string;
}> = ({ language, title, fileName, filePath, code }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: '#2d333b' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 0.75, bgcolor: '#1c2128', borderBottom: 1, borderColor: '#2d333b' }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {fileName && (
            <Tooltip title={filePath || ''} disableHoverListener={!filePath} arrow>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ cursor: filePath ? 'pointer' : 'default' }}
              >
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 14, color: '#90caf9' }} />
                <Typography variant="caption" fontWeight={600} sx={{ color: '#90caf9' }}>
                  {fileName}
                </Typography>
              </Stack>
            </Tooltip>
          )}
          {title && (
            <Typography variant="caption" fontWeight={600} sx={{ color: '#90caf9' }}>
              {title}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: '#768390' }}>
            {language}
          </Typography>
        </Stack>
        <Tooltip title={copied ? 'コピーしました' : 'コピー'}>
          <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5, color: '#768390' }}>
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      <SyntaxHighlighter
        language={language}
        style={oneDark}
        customStyle={{ margin: 0, padding: '12px', fontSize: '0.78rem', background: '#0d1117' }}
        wrapLongLines
      >
        {code}
      </SyntaxHighlighter>
    </Box>
  );
};

/** link 参照時の状態表示（ローディング / 不在）用の枠 */
const CodeMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: '#2d333b' }}>
    <Box
      sx={{
        px: 1.5,
        py: 2,
        bgcolor: '#0d1117',
        color: '#768390',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        fontSize: '0.78rem',
      }}
    >
      {children}
    </Box>
  </Box>
);

/**
 * link 参照モード: fileId から file_detail を取得して動的に表示する。
 * 参照先ファイルを編集すれば、プレビューを再表示したときに最新内容が反映される。
 *
 * 既定では「取得ファイル名 → title」「取得内容 → text」「拡張子 → language」を使うが、
 * param で明示された値（paramTitle / paramText / paramLanguage）があればそちらを優先する。
 */
const LinkedCodeBlock: React.FC<{
  fileId: string;
  paramTitle?: string;
  paramText?: string;
  paramLanguage?: string;
}> = ({ fileId, paramTitle, paramText, paramLanguage }) => {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'ready'; code: string; language: string; fileName: string; filePath?: string }
  >({ status: 'loading' });

  // 参照先ファイル取得 resolver（記事詳細では公開用API、未提供なら認証必須APIにフォールバック）
  const resolver = useKnzFileResolver();

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    const fetchFile = resolver
      ? resolver(fileId)
      : getFileDetail(fileId).then((detail) => ({
          fileId: detail.fileId,
          extension: detail.extension,
          detail: detail.detail,
          name: detail.name,
          path: detail.path,
        }));
    fetchFile
      .then((file) => {
        if (!active) return;
        setState({
          status: 'ready',
          code: file.detail ?? '',
          language: languageFromExtension(file.extension),
          // 公開用APIはファイル名を返さないため、無ければ拡張子由来の表示にフォールバック
          fileName: file.name ?? `参照ファイル.${file.extension}`,
          // 絶対パスはワークスペース経由（API6106）のみ取得できる。公開用APIでは undefined
          filePath: file.path,
        });
      })
      .catch(() => {
        // 取得失敗（ファイル不在・記事化されていない参照先など）はエラー画面にせず、枠内にメッセージ表示する
        if (active) setState({ status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [fileId, resolver]);

  if (state.status === 'loading') {
    return (
      <CodeMessage>
        <CircularProgress size={16} sx={{ color: '#768390' }} />
        参照先のファイルを読み込み中...
      </CodeMessage>
    );
  }
  if (state.status === 'error') {
    return <CodeMessage>参照先のファイルが存在しません（link: {fileId}）</CodeMessage>;
  }
  // language / text は param 優先、無ければ取得値（内容 / 拡張子言語）。
  // ヘッダーの見出しは従来どおり「paramTitle があればそれ、無ければ参照ファイル名」を1つだけ出す。
  // 参照ファイル名を出すケース（paramTitle なし）では fileName スロットに出し、
  // 絶対パス（filePath）をホバーでツールチップ表示する。
  return (
    <CodeFrame
      language={paramLanguage ?? state.language}
      fileName={paramTitle ? undefined : state.fileName}
      filePath={state.filePath}
      title={paramTitle}
      code={paramText && paramText !== '' ? paramText : state.code}
    />
  );
};

/**
 * コードブロック（WorkflowCodeBlock ベース + Prism ハイライト）。
 * params: language（任意）、title（任意）、link（任意, ファイルの fileId）、text（raw）
 *
 * link を指定すると、その fileId のファイル内容を取得して動的に表示する
 * （参照先ファイルを編集すれば内容も追従する）。
 * 既定では「取得ファイル名 → title」「取得内容 → text」「拡張子 → language」を使い、
 * param で title / text / language が明示されていればそちらを優先する。
 * ダーク背景固定でどのカラーモードでも視認性を確保する。
 */
export const CodeBlock: React.FC<KnzComponentProps> = ({ node }) => {
  const title = node.params.title as string | undefined;
  const link = node.params.link as string | undefined;
  const languageParam = node.params.language as string | undefined;
  const code = (node.params.text as string) ?? '';

  // link 指定時は動的取得モード。title / text / language は param があれば優先する。
  if (link) {
    return (
      <LinkedCodeBlock
        fileId={link}
        paramTitle={title}
        paramText={code}
        paramLanguage={languageParam}
      />
    );
  }

  // link なし: 従来どおり param の値で静的表示する。
  return <CodeFrame language={languageParam || 'text'} title={title} code={code} />;
};

export default CodeBlock;
